import { router, useForm } from '@inertiajs/react'
import type CrmPeopleIndividualDonor from '@/types/serializers/CrmPeopleIndividualDonor'
import type CrmPeopleOrganizationDonor from '@/types/serializers/CrmPeopleOrganizationDonor'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { toast } from 'sonner'

interface DonorDetailOverviewProps {
  donor: CrmPeopleIndividualDonor | CrmPeopleOrganizationDonor
  donorType: 'individual' | 'organization'
  isEditing?: boolean
  onEditSaved?: () => void
  onEditCancelled?: () => void
  onStatusChanged?: () => void
}

function formatDate(dateString?: string | null): string {
  if (!dateString) return '—'
  return new Date(dateString).toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

function formatAddress(
  address: { line1: string | null; line2: string | null; city: string | null; postal_code: string | null; country: string | null } | null
): string {
  if (!address) return '—'
  const parts = [address.line1, address.line2, address.city, address.postal_code, address.country].filter(Boolean)
  return parts.length > 0 ? parts.join(', ') : '—'
}

function StatusBadge({ status }: { status: string | { value: number; label: string } }) {
  if (typeof status === 'object' && status !== null) {
    const variantByValue: Record<number, 'outline' | 'default' | 'secondary'> = {
      0: 'outline',
      1: 'default',
      2: 'secondary',
    }
    return (
      <Badge variant={variantByValue[status.value] ?? 'outline'}>
        {status.label}
      </Badge>
    )
  }
  const variantMap: Record<string, 'outline' | 'default' | 'secondary'> = {
    prospect: 'outline',
    active: 'default',
    inactive: 'secondary',
  }
  const labelMap: Record<string, string> = {
    prospect: 'Prospect',
    active: 'Actif',
    inactive: 'Inactif',
  }
  return (
    <Badge variant={variantMap[status] ?? 'outline'}>
      {labelMap[status] ?? status}
    </Badge>
  )
}

function ClickableStatusBadge({ status, donorType, donorId, onStatusChanged }: {
  status: string | { value: number; label: string }
  donorType: 'individual' | 'organization'
  donorId: number
  onStatusChanged?: () => void
}) {
  const statuses = [
    { value: 'prospect', label: 'Prospect' },
    { value: 'active', label: 'Actif' },
    { value: 'inactive', label: 'Inactif' },
  ]

  function handleStatusChange(newStatus: string) {
    const url = donorType === 'individual'
      ? `/people/individual_donors/${donorId}`
      : `/people/organization_donors/${donorId}`
    const paramKey = donorType === 'individual' ? 'individual_donor' : 'organization_donor'

    router.patch(url, { [paramKey]: { status: newStatus } }, {
      preserveScroll: true,
      preserveState: true,
      onSuccess: () => {
        onStatusChanged?.()
        toast.success('Statut mis a jour')
      }
    })
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="cursor-pointer">
          <StatusBadge status={status} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {statuses.map((s) => (
          <DropdownMenuItem key={s.value} onClick={() => handleStatusChange(s.value)}>
            {s.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-sm font-medium text-muted-foreground">{label}</p>
      <div className="text-sm mt-0.5">{children}</div>
    </div>
  )
}

function IndividualDonorEditForm({ donor, onSaved, onCancelled }: {
  donor: CrmPeopleIndividualDonor
  onSaved?: () => void
  onCancelled?: () => void
}) {
  const { data, setData, patch, processing, errors } = useForm({
    individual_donor: {
      first_name: donor.first_name ?? '',
      last_name: donor.last_name ?? '',
      email: donor.email ?? '',
      phone: donor.phone ?? '',
      address_line1: donor.address?.line1 ?? '',
      address_line2: donor.address?.line2 ?? '',
      city: donor.address?.city ?? '',
      postal_code: donor.address?.postal_code ?? '',
      country: donor.address?.country ?? '',
    }
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    patch(`/people/individual_donors/${donor.id}`, {
      preserveScroll: true,
      preserveState: true,
      onSuccess: () => onSaved?.()
    })
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="edit-ind-first" className="text-xs">
            Prenom <span className="text-destructive">*</span>
          </Label>
          <Input
            id="edit-ind-first"
            value={data.individual_donor.first_name}
            onChange={(e) => setData('individual_donor', { ...data.individual_donor, first_name: e.target.value })}
            placeholder="Prenom"
            className="h-8 text-sm"
          />
          {errors['individual_donor.first_name'] && (
            <p className="text-xs text-destructive mt-1">{errors['individual_donor.first_name']}</p>
          )}
        </div>
        <div>
          <Label htmlFor="edit-ind-last" className="text-xs">
            Nom <span className="text-destructive">*</span>
          </Label>
          <Input
            id="edit-ind-last"
            value={data.individual_donor.last_name}
            onChange={(e) => setData('individual_donor', { ...data.individual_donor, last_name: e.target.value })}
            placeholder="Nom"
            className="h-8 text-sm"
          />
          {errors['individual_donor.last_name'] && (
            <p className="text-xs text-destructive mt-1">{errors['individual_donor.last_name']}</p>
          )}
        </div>
        <div>
          <Label htmlFor="edit-ind-email" className="text-xs">Email</Label>
          <Input
            id="edit-ind-email"
            type="email"
            value={data.individual_donor.email}
            onChange={(e) => setData('individual_donor', { ...data.individual_donor, email: e.target.value })}
            placeholder="email@exemple.com"
            className="h-8 text-sm"
          />
          {errors['individual_donor.email'] && (
            <p className="text-xs text-destructive mt-1">{errors['individual_donor.email']}</p>
          )}
        </div>
        <div>
          <Label htmlFor="edit-ind-phone" className="text-xs">Telephone</Label>
          <Input
            id="edit-ind-phone"
            value={data.individual_donor.phone}
            onChange={(e) => setData('individual_donor', { ...data.individual_donor, phone: e.target.value })}
            placeholder="+216 XX XXX XXX"
            className="h-8 text-sm"
          />
          {errors['individual_donor.phone'] && (
            <p className="text-xs text-destructive mt-1">{errors['individual_donor.phone']}</p>
          )}
        </div>
        <div className="col-span-2">
          <Label htmlFor="edit-ind-addr1" className="text-xs">Adresse ligne 1</Label>
          <Input
            id="edit-ind-addr1"
            value={data.individual_donor.address_line1}
            onChange={(e) => setData('individual_donor', { ...data.individual_donor, address_line1: e.target.value })}
            placeholder="Rue, numero"
            className="h-8 text-sm"
          />
        </div>
        <div className="col-span-2">
          <Label htmlFor="edit-ind-addr2" className="text-xs">Adresse ligne 2</Label>
          <Input
            id="edit-ind-addr2"
            value={data.individual_donor.address_line2}
            onChange={(e) => setData('individual_donor', { ...data.individual_donor, address_line2: e.target.value })}
            placeholder="Complement d'adresse"
            className="h-8 text-sm"
          />
        </div>
        <div>
          <Label htmlFor="edit-ind-city" className="text-xs">Ville</Label>
          <Input
            id="edit-ind-city"
            value={data.individual_donor.city}
            onChange={(e) => setData('individual_donor', { ...data.individual_donor, city: e.target.value })}
            placeholder="Tunis"
            className="h-8 text-sm"
          />
        </div>
        <div>
          <Label htmlFor="edit-ind-postal" className="text-xs">Code postal</Label>
          <Input
            id="edit-ind-postal"
            value={data.individual_donor.postal_code}
            onChange={(e) => setData('individual_donor', { ...data.individual_donor, postal_code: e.target.value })}
            placeholder="1000"
            className="h-8 text-sm"
          />
        </div>
        <div>
          <Label htmlFor="edit-ind-country" className="text-xs">Pays</Label>
          <Input
            id="edit-ind-country"
            value={data.individual_donor.country}
            onChange={(e) => setData('individual_donor', { ...data.individual_donor, country: e.target.value })}
            placeholder="Tunisie"
            className="h-8 text-sm"
          />
        </div>
      </div>
      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={processing}>Enregistrer</Button>
        <Button type="button" variant="outline" size="sm" onClick={onCancelled}>Annuler</Button>
      </div>
    </form>
  )
}

function OrganizationDonorEditForm({ donor, onSaved, onCancelled }: {
  donor: CrmPeopleOrganizationDonor
  onSaved?: () => void
  onCancelled?: () => void
}) {
  const { data, setData, patch, processing, errors } = useForm({
    organization_donor: {
      organization_name: donor.organization_name ?? '',
      email: donor.email ?? '',
      phone: donor.phone ?? '',
      tax_id: donor.tax_id ?? '',
      legal_form: donor.legal_form ?? '',
      sector: donor.sector ?? '',
      website: donor.website ?? '',
      address_line1: donor.address?.line1 ?? '',
      address_line2: donor.address?.line2 ?? '',
      city: donor.address?.city ?? '',
      postal_code: donor.address?.postal_code ?? '',
      country: donor.address?.country ?? '',
    }
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    patch(`/people/organization_donors/${donor.id}`, {
      preserveScroll: true,
      preserveState: true,
      onSuccess: () => onSaved?.()
    })
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <Label htmlFor="edit-org-name" className="text-xs">
            Nom de l'organisation <span className="text-destructive">*</span>
          </Label>
          <Input
            id="edit-org-name"
            value={data.organization_donor.organization_name}
            onChange={(e) => setData('organization_donor', { ...data.organization_donor, organization_name: e.target.value })}
            placeholder="Nom de l'organisation"
            className="h-8 text-sm"
          />
          {errors['organization_donor.organization_name'] && (
            <p className="text-xs text-destructive mt-1">{errors['organization_donor.organization_name']}</p>
          )}
        </div>
        <div>
          <Label htmlFor="edit-org-email" className="text-xs">Email</Label>
          <Input
            id="edit-org-email"
            type="email"
            value={data.organization_donor.email}
            onChange={(e) => setData('organization_donor', { ...data.organization_donor, email: e.target.value })}
            placeholder="email@exemple.com"
            className="h-8 text-sm"
          />
        </div>
        <div>
          <Label htmlFor="edit-org-phone" className="text-xs">Telephone</Label>
          <Input
            id="edit-org-phone"
            value={data.organization_donor.phone}
            onChange={(e) => setData('organization_donor', { ...data.organization_donor, phone: e.target.value })}
            placeholder="+216 XX XXX XXX"
            className="h-8 text-sm"
          />
        </div>
        <div>
          <Label htmlFor="edit-org-taxid" className="text-xs">Numero fiscal</Label>
          <Input
            id="edit-org-taxid"
            value={data.organization_donor.tax_id}
            onChange={(e) => setData('organization_donor', { ...data.organization_donor, tax_id: e.target.value })}
            placeholder="123456A"
            className="h-8 text-sm"
          />
        </div>
        <div>
          <Label htmlFor="edit-org-legal" className="text-xs">Forme juridique</Label>
          <Input
            id="edit-org-legal"
            value={data.organization_donor.legal_form}
            onChange={(e) => setData('organization_donor', { ...data.organization_donor, legal_form: e.target.value })}
            placeholder="SARL, SA..."
            className="h-8 text-sm"
          />
        </div>
        <div>
          <Label htmlFor="edit-org-sector" className="text-xs">Secteur</Label>
          <Input
            id="edit-org-sector"
            value={data.organization_donor.sector}
            onChange={(e) => setData('organization_donor', { ...data.organization_donor, sector: e.target.value })}
            placeholder="Technologie, ONG..."
            className="h-8 text-sm"
          />
        </div>
        <div>
          <Label htmlFor="edit-org-website" className="text-xs">Site web</Label>
          <Input
            id="edit-org-website"
            value={data.organization_donor.website}
            onChange={(e) => setData('organization_donor', { ...data.organization_donor, website: e.target.value })}
            placeholder="https://exemple.com"
            className="h-8 text-sm"
          />
        </div>
        <div className="col-span-2">
          <Label htmlFor="edit-org-addr1" className="text-xs">Adresse ligne 1</Label>
          <Input
            id="edit-org-addr1"
            value={data.organization_donor.address_line1}
            onChange={(e) => setData('organization_donor', { ...data.organization_donor, address_line1: e.target.value })}
            placeholder="Rue, numero"
            className="h-8 text-sm"
          />
        </div>
        <div className="col-span-2">
          <Label htmlFor="edit-org-addr2" className="text-xs">Adresse ligne 2</Label>
          <Input
            id="edit-org-addr2"
            value={data.organization_donor.address_line2}
            onChange={(e) => setData('organization_donor', { ...data.organization_donor, address_line2: e.target.value })}
            placeholder="Complement d'adresse"
            className="h-8 text-sm"
          />
        </div>
        <div>
          <Label htmlFor="edit-org-city" className="text-xs">Ville</Label>
          <Input
            id="edit-org-city"
            value={data.organization_donor.city}
            onChange={(e) => setData('organization_donor', { ...data.organization_donor, city: e.target.value })}
            placeholder="Tunis"
            className="h-8 text-sm"
          />
        </div>
        <div>
          <Label htmlFor="edit-org-postal" className="text-xs">Code postal</Label>
          <Input
            id="edit-org-postal"
            value={data.organization_donor.postal_code}
            onChange={(e) => setData('organization_donor', { ...data.organization_donor, postal_code: e.target.value })}
            placeholder="1000"
            className="h-8 text-sm"
          />
        </div>
        <div>
          <Label htmlFor="edit-org-country" className="text-xs">Pays</Label>
          <Input
            id="edit-org-country"
            value={data.organization_donor.country}
            onChange={(e) => setData('organization_donor', { ...data.organization_donor, country: e.target.value })}
            placeholder="Tunisie"
            className="h-8 text-sm"
          />
        </div>
      </div>
      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={processing}>Enregistrer</Button>
        <Button type="button" variant="outline" size="sm" onClick={onCancelled}>Annuler</Button>
      </div>
    </form>
  )
}

export function DonorDetailOverview({
  donor,
  donorType,
  isEditing,
  onEditSaved,
  onEditCancelled,
  onStatusChanged,
}: DonorDetailOverviewProps) {
  const isIndividual = donorType === 'individual'
  const indDonor = isIndividual ? (donor as CrmPeopleIndividualDonor) : null
  const orgDonor = !isIndividual ? (donor as CrmPeopleOrganizationDonor) : null

  if (isEditing) {
    if (isIndividual && indDonor) {
      return (
        <IndividualDonorEditForm
          donor={indDonor}
          onSaved={onEditSaved}
          onCancelled={onEditCancelled}
        />
      )
    }
    if (!isIndividual && orgDonor) {
      return (
        <OrganizationDonorEditForm
          donor={orgDonor}
          onSaved={onEditSaved}
          onCancelled={onEditCancelled}
        />
      )
    }
  }

  return (
    <div className="grid grid-cols-2 gap-4">
      <Field label="Nom">
        {isIndividual ? indDonor?.full_name ?? '—' : orgDonor?.organization_name ?? '—'}
      </Field>
      <Field label="Statut">
        <ClickableStatusBadge
          status={donor.status}
          donorType={donorType}
          donorId={donor.id}
          onStatusChanged={onStatusChanged}
        />
      </Field>
      <Field label="Email">{donor.email ?? '—'}</Field>
      <Field label="Telephone">{donor.phone ?? '—'}</Field>
      <Field label="Equipe">{donor.team_name ?? '—'}</Field>
      <Field label="Responsable">{donor.assignee_name ?? '—'}</Field>
      <Field label="Adresse">
        <span className="col-span-2">{formatAddress(donor.address)}</span>
      </Field>
      <Field label="Cree le">{formatDate(donor.created_at)}</Field>

      {orgDonor && (
        <>
          {orgDonor.tax_id && <Field label="Numero fiscal">{orgDonor.tax_id}</Field>}
          {orgDonor.legal_form && <Field label="Forme juridique">{orgDonor.legal_form}</Field>}
          {orgDonor.sector && <Field label="Secteur">{orgDonor.sector}</Field>}
          {orgDonor.website && (
            <Field label="Site web">
              <a
                href={orgDonor.website}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                {orgDonor.website}
              </a>
            </Field>
          )}
          <Field label="Contacts">{orgDonor.contact_count}</Field>
        </>
      )}
    </div>
  )
}
